import { Permission } from '../api/roles.dao';

export const USER_PERMISSIONS = 'USER_PERMISSIONS';

export const getAllPermissions = (selectedPermissions: Permission[] = []) => {
    const permissions = new Set(selectedPermissions);
    return [
        {
            title: 'Admin',
            id: 'oe.common.admin',
            permissions: [
                {
                    title: 'Accounts List',
                    id: Permission.ACCOUNTS_LIST,
                    intlId: 'oe.permission.accountsList',
                    status: permissions.has(Permission.ACCOUNTS_LIST),
                    description: 'Allows the user to view the list of Accounts assigned to them.'
                },
                {
                    title: 'Account Groups',
                    id: Permission.ACCOUNT_GROUPS_LIST,
                    intlId: 'oe.permission.accountGroupsList',
                    status: permissions.has(Permission.ACCOUNT_GROUPS_LIST),
                    description: 'Allows the user to view the list of Account Groups.'
                },
                {
                    title: 'Event Tracker',
                    id: Permission.EVENT_TRACKER,
                    intlId: 'oe.permission.eventTracker',
                    status: permissions.has(Permission.EVENT_TRACKER),
                    description: 'Allows the user to see and manage the Event Tracker.'
                },
                {
                    title: 'Resources',
                    id: Permission.RESOURCES,
                    intlId: 'oe.permission.resources',
                    status: permissions.has(Permission.RESOURCES),
                    description: 'Allow the user to view and manage Zoom Meeting and Zoom User resources.'
                },
                {
                    title: 'Refresh Host Keys',
                    id: Permission.REFRESH_HOST_KEYS,
                    intlId: 'oe.permission.resources',
                    status: permissions.has(Permission.REFRESH_HOST_KEYS),
                    description: 'Allows the user to refresh Zoom Account Host Keys, both individually and en masse.'
                },
                {
                    title: 'Operators',
                    id: Permission.OPERATORS,
                    intlId: 'oe.permission.operators',
                    status: permissions.has(Permission.OPERATORS),
                    description: 'Allows the user to see and manage Operators.'
                },
                {
                    title: 'User tab',
                    id: Permission.USER_TAB,
                    intlId: 'oe.permission.userTab',
                    status: permissions.has(Permission.USER_TAB),
                    description: 'Allows the user to view the Users tab, giving them access to the full list of OE Central users.  ' +
                        'This permission is for Administrators only.'
                },
                {
                    title: 'Roles',
                    id: Permission.ROLES,
                    intlId: 'oe.permission.roles',
                    status: permissions.has(Permission.ROLES),
                    description: 'Allows the user to see and manage Roles, for permission management.'
                },
                {
                    title: 'Themes',
                    id: Permission.THEMES,
                    intlId: 'oe.permission.themes',
                    status: permissions.has(Permission.THEMES),
                    description: 'Allows the user to see and manage Custom Passport Themes.'
                },
                {
                    title: 'System User Management',
                    id: Permission.SYSTEM_USER_MANAGEMENT,
                    intlId: 'oe.permission.systemUserManagement',
                    status: permissions.has(Permission.SYSTEM_USER_MANAGEMENT),
                    description: 'Allows the user to manage System Users.  System Users are critical for application testing, integrations, and other system functions.  ' +
                        'They should only be managed by System Admins.  This does not include Support users who manage normal user profiles.'
                },
                {
                    title: 'Telephone Number Sets',
                    id: Permission.TELEPHONE_NUMBER_SETS,
                    intlId: 'oe.permission.telephoneNumberSets',
                    status: permissions.has(Permission.TELEPHONE_NUMBER_SETS),
                    description: 'Allow the user to view and manage Telephone Number Sets.'
                }
            ]
        },
        {
            title: 'Account Management',
            id: 'oe.permission.accountManagement',
            permissions: [
                {
                    title: 'Create New Account',
                    id: Permission.CREATE_NEW_ACCOUNT,
                    intlId: 'oe.permission.createNewAccount',
                    status: permissions.has(Permission.CREATE_NEW_ACCOUNT),
                    description: 'Controls ability to create a new Account.'
                },
                {
                    title: 'Edit Account',
                    id: Permission.EDIT_ACCOUNT,
                    intlId: 'oe.permission.editAccount',
                    status: permissions.has(Permission.EDIT_ACCOUNT),
                    description: 'Controls ability to edit an Account.'
                },
                {
                    title: 'Delete Account',
                    id: Permission.DELETE_ACCOUNT,
                    intlId: 'oe.permission.deleteAccount',
                    status: permissions.has(Permission.DELETE_ACCOUNT),
                    description: 'Controls ability to delete an Account.'
                },
                {
                    title: 'Create New Account Group',
                    id: Permission.CREATE_ACCOUNT_GROUP_LIST,
                    intlId: 'oe.permission.createAccountGroup',
                    status: permissions.has(Permission.CREATE_ACCOUNT_GROUP_LIST),
                    description: 'Controls ability to create an Account Group.'
                },
                {
                    title: 'Edit Account Group',
                    id: Permission.EDIT_ACCOUNT_GROUP_LIST,
                    intlId: 'oe.permission.editAccountGroup',
                    status: permissions.has(Permission.EDIT_ACCOUNT_GROUP_LIST),
                    description: 'Controls access to edit existing Account Groups.'
                },
                {
                    title: 'Delete Account Group',
                    id: Permission.DELETE_ACCOUNT_GROUP_LIST,
                    intlId: 'oe.permission.deleteAccountGroup',
                    status: permissions.has(Permission.DELETE_ACCOUNT_GROUP_LIST),
                    description: 'Controls access to delete an Account Group.'
                }
            ]
        },
        {
            title: 'Account Administration',
            id: 'oe.common.accountAdministration',
            permissions: [
                {
                    title: 'User Group Admin',
                    id: Permission.USER_GROUP_ADMIN,
                    intlId: 'oe.permission.userGroups',
                    status: permissions.has(Permission.USER_GROUP_ADMIN),
                    description: 'Allows the user to see and manage User Groups.'
                },
                {
                    title: 'Account Admin tab',
                    id: Permission.ACCOUNT_ADMIN_TAB,
                    intlId: 'oe.permission.accountAdminTab',
                    status: permissions.has(Permission.ACCOUNT_ADMIN_TAB),
                    description: 'Allows the user to access the Account Admin tab.'
                },
                {
                    title: 'Account Users Tab',
                    id: Permission.ACCOUNT_USERS_TAB,
                    intlId: 'oe.permission.accountUsersTab',
                    status: permissions.has(Permission.ACCOUNT_USERS_TAB),
                    description: 'Allows the user to access the Account Users tab to view the list of Users with access to the Account.'
                },
                {
                    title:'Email Templates Tab',
                    id: Permission.EMAIL_TEMPLATES_TAB,
                    intlId: 'oe.permission.emailTemplates',
                    status: permissions.has(Permission.EMAIL_TEMPLATES_TAB),
                    description: 'Allows the user to view the list of Email Templates in the Account.'
                },
                {
                    title:'Create Email Template',
                    id: Permission.CREATE_EMAIL_TEMPLATES,
                    intlId: 'oe.permission.createEmailTemplate',
                    status: permissions.has(Permission.CREATE_EMAIL_TEMPLATES),
                    description: 'Allows the user to create a new email template in the Account.'
                },
                {
                    title:'Edit Email Template',
                    id: Permission.EDIT_EMAIL_TEMPLATES,
                    intlId: 'oe.permission.editEmailTemplate',
                    status: permissions.has(Permission.EDIT_EMAIL_TEMPLATES),
                    description: 'Allows the user to edit an email template.'
                },
                {
                    title:'Delete Email Template',
                    id: Permission.DELETE_EMAIL_TEMPLATES,
                    intlId: 'oe.permission.deleteEmailTemplate',
                    status: permissions.has(Permission.DELETE_EMAIL_TEMPLATES),
                    description: 'Allows the user to delete an email template.'
                },
                {
                    title: 'Manage Segregated Data Encryption',
                    id: Permission.MANAGE_SEGREGATED_DATA_ENCRYPTION,
                    intlId: 'oe.permission.manageSegregatedDataEncryption',
                    status: permissions.has(Permission.MANAGE_SEGREGATED_DATA_ENCRYPTION),
                    description: 'Allows the user to configure Segregated Data Encryption for an Account.'
                },
                {
                    title: 'Manage Data Anonymization',
                    id: Permission.MANAGE_DATA_ANONYMIZATION,
                    intlId: 'oe.permission.manageDataAnonymization',
                    status: permissions.has(Permission.MANAGE_DATA_ANONYMIZATION),
                    description: 'Allows the user to manage data anonymization.'
                },
                {
                    title: 'Manage 3rd Party Event Registration',
                    id: Permission.MANAGE_THIRD_PARTY_REGISTRATION,
                    intlId: 'oe.permission.manage3rdPartyEventRegistration',
                    status: permissions.has(Permission.MANAGE_THIRD_PARTY_REGISTRATION),
                    description: 'Allows the user to enable or disable the 3rd Party Event Registration feature.'
                },
                {
                    title: 'Configure Recurring Zoom Meetings',
                    id: Permission.CONFIGURE_RECURRING_ZOOM_MEETINGS,
                    intlId: 'oe.permission.configureRecurringZoomMeetings',
                    status: permissions.has(Permission.CONFIGURE_RECURRING_ZOOM_MEETINGS),
                    description: 'Allows the user to enable, disable and configure Recurring Zoom Meetings in the account.'
                },
                {
                    title: 'Manage Compliance Emails',
                    id: Permission.COMPLAINCE_EMAILS,
                    intlId: 'oe.permission.manageComplianceEmails',
                    status: permissions.has(Permission.COMPLAINCE_EMAILS),
                    description: 'Allows the user to enable and configure the compliance email capture feature.'
                },
                {
                    title: 'Manage Account Subdomain',
                    id: Permission.MANAGE_ACCOUNT_SUBDOMAIN,
                    intlId: 'oe.permission.manageAccountSubdomain',
                    status: permissions.has(Permission.MANAGE_ACCOUNT_SUBDOMAIN),
                    description: 'Allows the user to enable and edit the Account Subdomain for an Account.'
                },
                {
                    title: 'Manage Registration Approval Via API',
                    id: Permission.MANAGE_REGISTRATION_APPROVAL_VIA_API,
                    intlId: 'oe.permission.registrationApprovalViaAPI',
                    status: permissions.has(Permission.MANAGE_REGISTRATION_APPROVAL_VIA_API),
                    description: 'Allows the user to enable registration approval via API for this account.'
                },
                {
                    title: 'Manage Order Booking',
                    id: Permission.MANAGE_ORDER_BOOKING,
                    intlId: 'oe.permission.manageOrderBooking',
                    status: permissions.has(Permission.MANAGE_ORDER_BOOKING),
                    description: 'Allows the user to manage Order Booking within the account.'
                },
                {
                    title: 'Manage URL Parameter Registration',
                    id: Permission.MANAGE_URL_PARAMETER_REGISTRATION,
                    intlId: 'oe.permission.manageURLParameterRegistration',
                    status: permissions.has(Permission.MANAGE_URL_PARAMETER_REGISTRATION),
                    description: 'Allows the user to enable / disable URL Parameter Registration for an account.'
                },
                {
                    title: 'View Event Attributes',
                    id: Permission.VIEW_EVENT_ATTRIBUTES,
                    intlId: 'oe.permission.viewEventAttributes',
                    status: permissions.has(Permission.VIEW_EVENT_ATTRIBUTES),
                    description: 'Allows the user to view event attributes for an account.'
                },
                {
                    title: 'Manage Event Attributes',
                    id: Permission.MANAGE_EVENT_ATTRIBUTES,
                    intlId: 'oe.permission.manageEventAttributes',
                    status: permissions.has(Permission.MANAGE_EVENT_ATTRIBUTES),
                    description: 'Allows the user to manage event attributes for an account.'
                },
                {
                    title: 'Manage AI Agent Access',
                    id: Permission.MANAGE_AI_AGENT_ACCESS,
                    intlId: 'oe.permission.manageAIAgentAccess',
                    status: permissions.has(Permission.MANAGE_AI_AGENT_ACCESS),
                    description: 'Allows the user to enable / disable AI Agent Access for an account.'
                },
                {
                    title: 'Manage SSO Configurations',
                    id: Permission.MANAGE_SSO_CONFIGURATIONS,
                    intlId: 'oe.permission.manageSSOConfiguration',
                    status: permissions.has(Permission.MANAGE_SSO_CONFIGURATIONS),
                    description: 'Allows the user to manage SSO configurations within the account.'
                },
            ]
        },
        {
            title: 'Blueprints',
            id: 'oe.common.blueprints',
            permissions: [
                {
                    title: 'Blueprints tab',
                    id: Permission.BLUEPRINTS_TAB,
                    intlId: 'oe.permission.BlueprintsTab',
                    status: permissions.has(Permission.BLUEPRINTS_TAB),
                    description: 'Allows the user to view the available Blueprints for an Account.'
                },
                {
                    title: 'Create Event Blueprints',
                    id: Permission.CREATE_EVENT_BLUEPRINTS,
                    intlId: 'oe.permission.createEventBlueprints',
                    status: permissions.has(Permission.CREATE_EVENT_BLUEPRINTS),
                    description: 'Allows the user to create Event Blueprints in an Account.'
                },
                {
                    title: 'Edit Event Blueprint',
                    id: Permission.EDIT_EVENT_BLUEPRINTS,
                    intlId: 'oe.permission.editEventBlueprints',
                    status: permissions.has(Permission.EDIT_EVENT_BLUEPRINTS),
                    description: 'Allows the user to edit an event Blueprint.'
                },
                {
                    title: 'Delete Event Blueprint',
                    id: Permission.DELETE_EVENT_BLUEPRINTS,
                    intlId: 'oe.permission.deleteEventBlueprints',
                    status: permissions.has(Permission.DELETE_EVENT_BLUEPRINTS),
                    description: 'Allows the user to permanently delete an Event Blueprint.'
                },
                {
                    title: 'Create Meeting Templates',
                    id: Permission.CREATE_MEETING_TEMPLATES,
                    intlId: 'oe.permission.createMeetingTemplates',
                    status: permissions.has(Permission.CREATE_MEETING_TEMPLATES),
                    description: 'Allows the user to create Meeting Templates in an Account.'
                },
                {
                    title: 'Edit Meeting Templates',
                    id: Permission.EDIT_MEETING_TEMPLATES,
                    intlId: 'oe.permission.editMeetingTemplates',
                    status: permissions.has(Permission.EDIT_MEETING_TEMPLATES),
                    description: 'Allows the user to edit a Meeting Template.'
                },
                {
                    title: 'Delete Meeting Template',
                    id: Permission.DELETE_MEETING_TEMPLATES,
                    intlId: 'oe.permission.deleteMeetingTemplates',
                    status: permissions.has(Permission.DELETE_MEETING_TEMPLATES),
                    description: 'Allows the user to permanently delete a Meeting Template.'
                },
                {
                    title: 'Create Connection Templates',
                    id: Permission.CREATE_CONNECTION_TEMPLATES,
                    intlId: 'oe.permission.createConnectionTemplates',
                    status: permissions.has(Permission.CREATE_CONNECTION_TEMPLATES),
                    description: 'Allows the user to create Connection Templates in an Account.'
                },
                {
                    title: 'Edit Connection Templates',
                    id: Permission.EDIT_CONNECTION_TEMPLATES,
                    intlId: 'oe.permission.editConnectionTemplates',
                    status: permissions.has(Permission.EDIT_CONNECTION_TEMPLATES),
                    description: 'Allows the user to edit a Connection Template.'
                },
                {
                    title: 'Delete Connection Templates',
                    id: Permission.DELETE_CONNECTION_TEMPLATES,
                    intlId: 'oe.permission.deleteConnectionTemplates',
                    status: permissions.has(Permission.DELETE_CONNECTION_TEMPLATES),
                    description: 'Allows the user to permanently delete a Connection Template.'
                },
                {
                    title: 'Create Order Template',
                    id: Permission.CREATE_ORDER_TEMPLATE,
                    intlId: 'oe.permission.createOrderTemplate',
                    status: permissions.has(Permission.CREATE_ORDER_TEMPLATE),
                    description: 'Allows the user to create Order Templates in an Account'
                },
                {
                    title: 'Edit Order Template',
                    id: Permission.EDIT_ORDER_TEMPLATE,
                    intlId: 'oe.permission.editOrderTemplate',
                    status: permissions.has(Permission.EDIT_ORDER_TEMPLATE),
                    description: 'Allows the user to edit an Order Template.'
                },
            ]
        },
        {
            title: 'Connections',
            id: 'oe.common.connections',
            permissions: [
                {
                    title: 'Create Auto Sync Connection',
                    id: Permission.CREATE_AUTOSYNC_CONNECTION,
                    intlId: 'oe.permission.createAutoSyncConnection',
                    status: permissions.has(Permission.CREATE_AUTOSYNC_CONNECTION),
                    description: 'Allows the user to enable Auto-Sync when adding a Connection which supports it.'
                },
                {
                    title: 'Connections Management',
                    id: Permission.CONNECTIONS_MANAGEMENT,
                    intlId: 'oe.permission.connectionManagement',
                    status: permissions.has(Permission.CONNECTIONS_MANAGEMENT),
                    description: 'Control access to the Connections tab, and to adding or editing Connections at Room or Meeting level.'
                },
                {
                    title: 'Edit Meeting Connections with Template',
                    id: Permission.EDIT_MEETING_CONNECTIONS_WITH_TEMPLATE,
                    intlId: 'oe.permission.editMeetingConnectionsWithTemplate',
                    status: permissions.has(Permission.EDIT_MEETING_CONNECTIONS_WITH_TEMPLATE),
                    description: 'Allows the user to select and apply a Connection Template when editing Meeting Connections. Note - Connections Management permission is also required.'
                },
                {
                    title: 'Create Audio Bridge Connection',
                    id: Permission.CREATE_AUDIO_BRIDGE_CONNECTION,
                    intlId: 'oe.permission.createAudioBridgeConnection',
                    status: permissions.has(Permission.CREATE_AUDIO_BRIDGE_CONNECTION),
                    description: 'Allows the user to Create Audio Bridge Connections.'
                },
                {
                    title: 'Create Zoom Connections',
                    id: Permission.CREATE_ZOOM_CONNECTIONS,
                    intlId: 'oe.permission.createZoomConnections',
                    status: permissions.has(Permission.CREATE_ZOOM_CONNECTIONS),
                    description: 'Controls ability to run Create Zoom Connections procedure.'
                },
                {
                    title: 'Create OE Streams',
                    id: Permission.CREATE_KNOVIO_LIVE_STREAMS,
                    intlId: 'oe.permission.createKnovioLiveStreams',
                    status: permissions.has(Permission.CREATE_KNOVIO_LIVE_STREAMS),
                    description: 'Allows the user to create new OE Streams via the integration between OE Stream and Central.'
                },
                {
                    title: 'Edit OE Streams',
                    id: Permission.EDIT_KNOVIO_LIVE_STREAMS,
                    intlId: 'oe.permission.editKnovioLiveStreams',
                    status: permissions.has(Permission.EDIT_KNOVIO_LIVE_STREAMS),
                    description: 'Allows the user to edit Live OE Streams via the integration between OE Stream and Central.'
                },
                {
                    title: 'Sync Speakers to OE Stream',
                    id: Permission.SYNC_SPEAKERS_TO_KNOVIO,
                    intlId: 'oe.permission.syncSpeakersToKnovio',
                    status: permissions.has(Permission.SYNC_SPEAKERS_TO_KNOVIO),
                    description: 'Allows the user to push meeting Presenters and Hosts to OE Stream as Speakers for a OE Stream Connection.'
                },
                {
                    title: 'Preview OE Stream Connection',
                    id: Permission.PREVIEW_KNOVIO_CONNECTION,
                    intlId: 'oe.permission.previewKnovioConnection',
                    status: permissions.has(Permission.PREVIEW_KNOVIO_CONNECTION),
                    description: 'Allows the user to generate a Preview link for a OE Stream Connection.'
                },
                {
                    title: 'Create Connection Groups',
                    id: Permission.CREATE_CONNECTION_GROUPS,
                    intlId: 'oe.permission.createConnectionGroups',
                    status: permissions.has(Permission.CREATE_CONNECTION_GROUPS),
                    description: 'Allows the user to create a new Connection Group.'
                },
                {
                    title: 'Edit Connection Groups',
                    id: Permission.EDIT_CONNECTION_GROUPS,
                    intlId: 'oe.permission.editConnectionGroups',
                    status: permissions.has(Permission.EDIT_CONNECTION_GROUPS),
                    description: 'Allows the user to edit a Connection Group.'
                },
                {
                    title: 'Delete Connection Groups',
                    id: Permission.DELETE_CONNECTION_GROUPS,
                    intlId: 'oe.permission.deleteConnectionGroups',
                    status: permissions.has(Permission.DELETE_CONNECTION_GROUPS),
                    description: 'Allows the user to delete a Connection Group.'
                },
                {
                    title: 'Host Management',
                    id: Permission.HOST_MANAGEMENT,
                    intlId: 'oe.permission.hostManagement',
                    status: permissions.has(Permission.HOST_MANAGEMENT),
                    description: 'Controls access to Host button and Host link field.'
                },
                {
                    title: 'Send Connections Email',
                    id: Permission.SEND_CONNECTIONS_EMAIL,
                    intlId: 'oe.permission.sendConnectionsEmail',
                    status: permissions.has(Permission.SEND_CONNECTIONS_EMAIL),
                    description: 'Allows the user to generate a Connections Email using any Template from the Account, edit the content of the email and send it from OE Central.'
                },
                {
                    title: 'View Meeting Links',
                    id: Permission.VIEW_MEETING_LINKS,
                    intlId: 'oe.permission.viewMeetingLinks',
                    status: permissions.has(Permission.VIEW_MEETING_LINKS),
                    description: 'Allows the user to see and copy meeting links.'
                },
                {
                    title: 'Manage Meeting Links',
                    id: Permission.MANAGE_MEETING_LINKS,
                    intlId: 'oe.permission.manageMeetingLinks',
                    status: permissions.has(Permission.MANAGE_MEETING_LINKS),
                    description: 'Allows the user to create and edit meeting links.'
                },
            ]
        },
        {
            title: 'OE Passport',
            id: 'oe.common.passport',
            permissions: [
                {
                    title: 'Passport tab',
                    id: Permission.PASSPORT_TAB,
                    intlId: 'oe.permission.passportTab',
                    status: permissions.has(Permission.PASSPORT_TAB),
                    description: 'Controls access to the Passport tab, for managing OE Passport sites for Events.'
                },
                {
                    title: 'View Passport URL',
                    id: Permission.VIEW_PASSPORT_LINK,
                    intlId: 'oe.permission.passportLink',
                    status: permissions.has(Permission.VIEW_PASSPORT_LINK),
                    description: 'Allows the user access to the Passport tab only to see and copy the Passport link.  In order to edit Passport settings, the Passport Tab permission is required.'
                },
                {
                    title: 'Edit Event Title and Description',
                    id: Permission.EDIT_EVENT_TITLE_AND_DESCRIPTION,
                    intlId: 'oe.permission.editEventTitleAndDescription',
                    status: permissions.has(Permission.EDIT_EVENT_TITLE_AND_DESCRIPTION),
                    description: 'Allows the user to edit the Event Title for Passport, Public Description and Private Description fields.'
                },
                {
                    title: 'Triaged Registration',
                    id: Permission.TRIAGED_REGISTRATION,
                    intlId: 'oe.permission.triagedRegistration',
                    status: permissions.has(Permission.TRIAGED_REGISTRATION),
                    description: 'Allows the user to set up different Registration Forms and Settings using a Triage Question.'
                },
                {
                    title: 'Generate Images with AI',
                    id: Permission.GENERATE_IMAGES_WITH_AI,
                    intlId: 'oe.permission.generateImagesWithAi',
                    status: permissions.has(Permission.GENERATE_IMAGES_WITH_AI),
                    description: 'Allows the user to generate images to be applied to the Passport site, using OE Central’s integration with AI models.'
                },
                {
                    title: 'Attendee SSO',
                    id: Permission.ATTENDEE_SSO,
                    intlId: 'oe.permission.attendeeSSO',
                    status: permissions.has(Permission.ATTENDEE_SSO),
                    description: 'Allows the user to manage attendee SSO in Passport.'
                },
            ]
        },
        {
            title: 'Event Management',
            id: 'oe.common.events',
            permissions: [
                {
                    title: 'Create New Event',
                    id: Permission.CREATE_NEW_EVENT,
                    intlId: 'oe.permission.createEvent',
                    status: permissions.has(Permission.CREATE_NEW_EVENT),
                    description: 'Controls ability to create a new Event.'
                },
                {
                    title: 'Create Event from Blueprint',
                    id: Permission.CREATE_EVENT_FROM_BLUEPRINT,
                    intlId: 'oe.permission.createEventFromBlueprint',
                    status: permissions.has(Permission.CREATE_EVENT_FROM_BLUEPRINT),
                    description: 'Allows the user to select a Blueprint when creating an Event. Note - Create Event permission is also required.'
                },
                {
                    title: 'Edit Event',
                    id: Permission.EDIT_EVENT,
                    intlId: 'oe.permission.editEvent',
                    status: permissions.has(Permission.EDIT_EVENT),
                    description: 'Controls ability to edit an Event.'
                },
                {
                    title: 'Edit Event with Blueprint',
                    id: Permission.EDIT_EVENT_FROM_BLUEPRINT,
                    intlId: 'oe.permission.editEventFromBlueprint',
                    status: permissions.has(Permission.EDIT_EVENT_FROM_BLUEPRINT),
                    description: 'Allows the user to apply a Blueprint when editing an event. Note - Edit Event permission is also required.'
                },
                {
                    title: 'Delete Event',
                    id: Permission.DELETE_EVENT,
                    intlId: 'oe.permission.deleteEvent',
                    status: permissions.has(Permission.DELETE_EVENT),
                    description: 'Controls ability to delete an Event.'
                },
                {
                    title: 'View internal event links',
                    id: Permission.VIEW_INTERNAL_EVENT_LINKS,
                    intlId: 'oe.permissions.viewInternalEventLinks',
                    status: permissions.has(Permission.VIEW_INTERNAL_EVENT_LINKS),
                    description: 'Controls access to Event Links, Salesforce, Guidelines and Project Plan links. ' +
                        'Access to the Passport link is not restricted by this permission. This permission is for OE staff members only.'
                },
                {
                    title: 'Planning Notes',
                    id: Permission.PLANNING_NOTES,
                    intlId: 'oe.permissions.planningNotes',
                    status: permissions.has(Permission.PLANNING_NOTES),
                    description: 'Controls ability to view and edit the Planning Notes field.'
                },
                {
                    title: 'Book an Event',
                    id: Permission.BOOK_AN_EVENT,
                    intlId: 'oe.permission.bookEvent',
                    status: permissions.has(Permission.BOOK_AN_EVENT),
                    description: 'Allows the User to book an Event.'
                },
            ]
        },
        {
            title: 'Order Management',
            id: 'oe.permission.orderManagement',
            permissions: [
                {
                    title: 'View Order',
                    id: Permission.VIEW_SALESFORCE_ORDER,
                    intlId: 'oe.permission.viewOrder',
                    status: permissions.has(Permission.VIEW_SALESFORCE_ORDER),
                    description: 'Controls the ability to view Order details and associated products for a linked event.'
                },
                {
                    title: 'Create Order',
                    id: Permission.CREATE_ORDER,
                    intlId: 'oe.permission.createOrder',
                    status: permissions.has(Permission.CREATE_ORDER),
                    description: 'Controls the ability to create Orders from within OE Central.'
                },
                {
                    title: 'Update Order',
                    id: Permission.UPDATE_ORDER,
                    intlId: 'oe.permission.updateOrder',
                    status: permissions.has(Permission.UPDATE_ORDER),
                    description: 'Controls the ability to edit, link and unlink Orders from within OE Central.'
                },
                {
                    title: 'Manage Products',
                    id: Permission.MANAGE_PRODUCTS,
                    intlId: 'oe.permission.manageProducts',
                    status: permissions.has(Permission.MANAGE_PRODUCTS),
                    description: 'Controls the ability to add, edit, and remove Products from OE Central.'
                },
                {
                    title: 'Override Billing Lock',
                    id: Permission.OVERRIDE_BILLING_LOCK,
                    intlId: 'oe.permission.overrideBillingLock',
                    status: permissions.has(Permission.OVERRIDE_BILLING_LOCK),
                    description: 'Allows editing Billing fields, Client Department, and Products on an Order after its Billing Status moves to Pending Billing Review or later, bypassing the normal lock.'
                },
            ]
        },
        {
            title: 'Schedule Management',
            id: 'oe.permission.scheduleManagement',
            permissions: [
                {
                    title: 'Schedule Tab',
                    id: Permission.SCHEDULE_TAB,
                    intlId: 'oe.permission.scheduleTab',
                    status: permissions.has(Permission.SCHEDULE_TAB),
                    description: 'Controls ability to view the Schedule tab for an Event.'
                },
                {
                    title: 'Create Meetings',
                    id: Permission.CREATE_NEW_MEETING,
                    intlId: 'oe.permission.createMeeting',
                    status: permissions.has(Permission.CREATE_NEW_MEETING),
                    description: 'Controls ability to create meetings on the Event.'
                },
                {
                    title: 'Create Meeting from Template',
                    id: Permission.CREATE_MEETING_FROM_TEMPLATE,
                    intlId: 'oe.permission.createMeetingFromTemplate',
                    status: permissions.has(Permission.CREATE_MEETING_FROM_TEMPLATE),
                    description: 'Allows the user to select a Meeting Template when creating a Meeting. Note - Create Meeting permission is also required.'
                },
                {
                    title: 'Create Meeting with AI Co-Pilot',
                    id: Permission.CREATE_MEETING_WITH_AI,
                    intlId: 'oe.permission.createMeetingWithAI',
                    status: permissions.has(Permission.CREATE_MEETING_WITH_AI),
                    description: 'Allows the user to create meetings using an AI-powered natural language prompt.'
                },
                {
                    title: 'Enable V2 Connections on a Meeting',
                    id: Permission.CREATE_MEETINGS_V2,
                    intlId: 'oe.permission.meetingConnectionsManagementV2',
                    status: permissions.has(Permission.CREATE_MEETINGS_V2),
                    description: 'Allows the user to enable the V2 Connections feature when creating a Meeting. Note - Create Meeting permission is also required.'
                },
                {
                    title: 'Update Meetings',
                    id: Permission.UPDATE_MEETING,
                    intlId: 'oe.permission.updateMeeting',
                    status: permissions.has(Permission.UPDATE_MEETING),
                    description: 'Controls ability to edit meetings on the Event.'
                },
                {
                    title: 'Delete Meetings',
                    id: Permission.DELETE_MEETING,
                    intlId: 'oe.permission.deleteMeeting',
                    status: permissions.has(Permission.DELETE_MEETING),
                    description: 'Controls ability to delete meetings on the Event.'
                },
                {
                    title: 'Schedule Upload',
                    id: Permission.SCHEDULE_UPLOAD,
                    intlId: 'oe.permission.scheduleUpload',
                    status: permissions.has(Permission.SCHEDULE_UPLOAD),
                    description: 'Controls ability to upload Schedule CSV file.'
                },
                {
                    title: 'Bulk Meeting Status Update',
                    id: Permission.BULK_MEETING_STATUS_UPDATE,
                    intlId: 'oe.permission.bulkMeetingStatusUpdate',
                    status: permissions.has(Permission.BULK_MEETING_STATUS_UPDATE),
                    description: 'Controls ability to apply bulk Cancellation and Removal for meetings.'
                },
                {
                    title: 'Schedule Download',
                    id: Permission.SCHEDULE_DOWNLOAD,
                    intlId: 'oe.permission.scheduleDownload',
                    status: permissions.has(Permission.SCHEDULE_DOWNLOAD),
                    description: 'Controls ability to download Schedule CSV file.'
                }
            ]
        },
        {
            title: 'Asset Management',
            id: 'oe.permission.assetManagement',
            permissions: [
                {
                    title: 'Assets Tab',
                    id: Permission.ASSETS_TAB,
                    intlId: 'oe.permission.assetsTab',
                    status: permissions.has(Permission.ASSETS_TAB),
                    description: 'Controls access to the Assets tab at Event level.'
                },
                {
                    title: 'Upload Event Assets',
                    id: Permission.UPLOAD_EVENT_ASSETS,
                    intlId: 'oe.permission.uploadEventAssets',
                    status: permissions.has(Permission.UPLOAD_EVENT_ASSETS),
                    description: 'Controls ability to upload an Asset to the Event.'
                },
                {
                    title: 'Download Event Assets',
                    id: Permission.DOWNLOAD_EVENT_ASSETS,
                    intlId: 'oe.permission.downloadEventAssets',
                    status: permissions.has(Permission.DOWNLOAD_EVENT_ASSETS),
                    description: 'Allows user to Download Assets. This permission is also required in order for users to drag and drop Uncategorized files onto Speakers.' +
                        ' User with only Edit Attendee permission will be able to update speaker profile images by clicking the Add button.'
                },
                {
                    title: 'Delete Event Assets',
                    id: Permission.DELETE_EVENT_ASSETS,
                    intlId: 'oe.permission.deleteEventAssets',
                    status: permissions.has(Permission.DELETE_EVENT_ASSETS),
                    description: 'Controls ability to Delete an Asset from an Event.'
                },
                {
                    title: 'Create Asset Download Link',
                    id: Permission.GENERATE_EVENT_ASSETS_PUBLIC_DOWNLOAD_LINK,
                    intlId: 'oe.permission.createAssetDownloadLink',
                    status: permissions.has(Permission.GENERATE_EVENT_ASSETS_PUBLIC_DOWNLOAD_LINK),
                    description: 'Allows the user to create public download links for event assets.'
                },
                {
                    title: 'Delete Asset Download Link',
                    id: Permission.DELETE_ASSET_DOWNLOAD_LINK,
                    intlId: 'oe.permission.deleteAssetDownloadLink',
                    status: permissions.has(Permission.DELETE_ASSET_DOWNLOAD_LINK),
                    description: 'Allows the user to delete public download links for event Assets.'
                },
            ]
        },
        {
            title: 'Artifacts Management',
            id: 'oe.permission.artifactsManagement',
            permissions: [
                {
                    title: 'Artifacts Tab',
                    id: Permission.ARTIFACTS_TAB,
                    intlId: 'oe.permission.artifactsTab',
                    status: permissions.has(Permission.ARTIFACTS_TAB),
                    description: 'Controls access to the Artifacts tab at Event level.'
                },
                {
                    title: 'Request Artifacts',
                    id: Permission.REQUEST_ARTIFACTS,
                    intlId: 'oe.permission.requestArtifacts',
                    status: permissions.has(Permission.REQUEST_ARTIFACTS),
                    description: 'Allows user to request any type of artifact to be generated for meeting(s), including transcripts, MP3s and MP4s. Only use this permission for internal users.'
                },
                {
                    title: 'Request AI Transcript',
                    id: Permission.REQUEST_AI_TRANSCRIPT,
                    intlId: 'oe.permission.requestAiTranscript',
                    status: permissions.has(Permission.REQUEST_AI_TRANSCRIPT),
                    description: 'Allows the user to request AI transcript artifacts only. Use this permission for client users who are entitled to free AI transcripts, but who should not be pulling other artifact types.'
                },
                {
                    title: 'Download Artifact',
                    id: Permission.DOWNLOAD_ARTIFACT,
                    intlId: 'oe.permission.downloadArtifact',
                    status: permissions.has(Permission.DOWNLOAD_ARTIFACT),
                    description: 'Allows the user to download artifact files.'
                },
                {
                    title: 'Delete Artifact',
                    id: Permission.DELETE_ARTIFACT,
                    intlId: 'oe.permission.deleteArtifact',
                    status: permissions.has(Permission.DELETE_ARTIFACT),
                    description: 'Allows the user to permanently delete an Artifact from the Event.'
                },
                {
                    title: 'Edit Artifact',
                    id: Permission.EDIT_ARTIFACT,
                    intlId: 'oe.permission.editArtifact',
                    status: permissions.has(Permission.EDIT_ARTIFACT),
                    description: 'Allows the user to update an Artifact including adding or editing an Artifact Note.'
                },
                {
                    title: 'Create Artifact Download Link',
                    id: Permission.CREATE_ARTIFACT_PUBLIC_DOWNLOAD_LINK,
                    intlId: 'oe.permission.createArtifactDownloadLink',
                    status: permissions.has(Permission.CREATE_ARTIFACT_PUBLIC_DOWNLOAD_LINK),
                    description: 'Allows the user to create public download links for event Artifacts.'
                },
                {
                    title: 'Delete Artifact Download Link',
                    id: Permission.DELETE_ARTIFACT_DOWNLOAD_LINK,
                    intlId: 'oe.permission.deleteArtifactDownloadLink',
                    status: permissions.has(Permission.DELETE_ARTIFACT_DOWNLOAD_LINK),
                    description: 'Allows the user to delete public download links for event Artifacts.'
                },
                {
                    title: 'Request Passport Replay',
                    id: Permission.REQUEST_PASSPORT_REPLAY,
                    intlId: 'oe.permission.requestPassportReplay',
                    status: permissions.has(Permission.REQUEST_PASSPORT_REPLAY),
                    description: 'Allows the user to request a Passport Replay OE Stream connection to be created for a meeting.'
                },
            ]
        },
        {
            title: 'Reports Management',
            id: 'oe.permission.reportsManagement',
            permissions: [
                {
                    title: 'Reports tab',
                    id: Permission.REPORTS_TAB,
                    intlId: 'oe.permission.reportsTab',
                    status: permissions.has(Permission.REPORTS_TAB),
                    description: 'Controls access to the Reports tab at Event level.'
                },
                {
                    title: 'Upload Report',
                    id: Permission.UPLOAD_REPORT,
                    intlId: 'oe.permission.uploadReport',
                    status: permissions.has(Permission.UPLOAD_REPORT),
                    description: 'Allows user to upload a new report.'
                },
                {
                    title: 'Edit Report',
                    id: Permission.EDIT_REPORT,
                    intlId: 'oe.permission.editReport',
                    status: permissions.has(Permission.EDIT_REPORT),
                    description: 'Controls ability to edit report names and types.'
                },
                {
                    title: 'Delete Report',
                    id: Permission.DELETE_REPORT,
                    intlId: 'oe.permission.deleteReport',
                    status: permissions.has(Permission.DELETE_REPORT),
                    description: 'Controls ability to delete reports.'
                },
                {
                    title: 'Event Interaction Report',
                    id: Permission.EVENT_INTERACTIONS_REPORT,
                    intlId: 'oe.permission.eventInteractiveReport',
                    status: permissions.has(Permission.EVENT_INTERACTIONS_REPORT),
                    description: 'Allows the user to generate the Event Interactions Report.'
                },
                {
                    title: 'Zoom Webinar Reports',
                    id: Permission.ZOOM_WEBINAR_REPORT,
                    intlId: 'oe.permission.zoomWebinarRegistrationReport',
                    status: permissions.has(Permission.ZOOM_WEBINAR_REPORT),
                    description: 'Controls ability to generate the Zoom Webinar Registration and Attendee reports.'
                },
                {
                    title: 'Registrants by Session Report',
                    id: Permission.GENERATE_REGISTRANTS_BY_SESSION,
                    intlId: 'oe.permission.registrantReportDownload',
                    status: permissions.has(Permission.GENERATE_REGISTRANTS_BY_SESSION),
                    description: 'Controls ability to download Registrants by Session Report.'
                },
                {
                    title: 'Meeting Registration Report',
                    id: Permission.MEETING_REGISTRATION_REPORT,
                    intlId: 'oe.permission.meetingRegistrationReport',
                    status: permissions.has(Permission.MEETING_REGISTRATION_REPORT),
                    description: 'Allows the user to generate a Meeting Registration Report.'
                },
                {
                    title: 'Meeting Interaction Report',
                    id: Permission.MEETING_INTERACTIONS_REPORT,
                    intlId: 'oe.permission.meetingInteractionReport',
                    status: permissions.has(Permission.MEETING_INTERACTIONS_REPORT),
                    description: 'Allows the user to download a Meeting Interaction Report.'
                },
                {
                    title: 'Attendees Interaction Report',
                    id: Permission.ATTENDEE_INTERACTIONS_REPORT,
                    intlId: 'oe.permission.attendeesInteractionReport',
                    status: permissions.has(Permission.ATTENDEE_INTERACTIONS_REPORT),
                    description: 'Allows the user to generate the Attendees Interaction Report.'
                },
                {
                    title: 'Event Registration Report',
                    id: Permission.EVENT_REGISTRATION_REPORT,
                    intlId: 'oe.permission.eventRegistrationReport',
                    status: permissions.has(Permission.EVENT_REGISTRATION_REPORT),
                    description: 'Allows the user to generate the Event Registration Report.'
                },
                {
                    title: 'Zoom Meeting Reports',
                    id: Permission.ZOOM_MEETING_REPORTS,
                    intlId: 'oe.permission.zoomMeetingReports',
                    status: permissions.has(Permission.ZOOM_MEETING_REPORTS),
                    description: 'Allows the user to generate a Zoom Meeting registration report, which pulls data from Zoom.'
                }
            ]
        },
        {
            title: 'Control Tab',
            id: 'oe.control.tabName',
            permissions: [
                {
                    title: 'Run Meeting',
                    id: Permission.RUN_MEETING,
                    intlId: 'oe.permission.runMeeting',
                    status: permissions.has(Permission.RUN_MEETING),
                    description: 'Control access to the Run Meeting popover.'
                },
                {
                    title: 'View Guidelines',
                    id: Permission.VIEW_GUIDELINES,
                    intlId: 'oe.permission.viewGuidelines',
                    status: permissions.has(Permission.VIEW_GUIDELINES),
                    description: 'Control access to View Guidelines.'
                }
            ]
        },
        {
            title: 'Rooms Management',
            id: 'oe.room.management',
            permissions: [
                {
                    title: 'Create New Room',
                    id: Permission.CREATE_ROOM,
                    intlId: 'oe.permission.createNewRoom',
                    status: permissions.has(Permission.CREATE_ROOM),
                    description: 'Controls ability to create a new Room.'
                },
                {
                    title: 'Enable V2 Connections on a Room',
                    id: Permission.CREATE_NEW_ROOM_V2,
                    intlId: 'oe.permission.meetingConnectionsManagementV2',
                    status: permissions.has(Permission.CREATE_NEW_ROOM_V2),
                    description: 'Allows the user to enable the V2 Connections feature when creating a Room. Note - Create New Room permission is also required.'
                },
                {
                    title: 'Edit Room',
                    id: Permission.EDIT_ROOM,
                    intlId: 'oe.permission.editRoom',
                    status: permissions.has(Permission.EDIT_ROOM),
                    description: 'Controls ability to edit Rooms.'
                },
                {
                    title: 'Delete Room',
                    id: Permission.DELETE_ROOM,
                    intlId: 'oe.permission.deleteRoom',
                    status: permissions.has(Permission.DELETE_ROOM),
                    description: 'Controls ability to delete Rooms.'
                },
                {
                    title: 'Create and Assign Room',
                    id: Permission.CREATE_AND_ASSIGN_ROOM,
                    intlId: 'oe.permission.createAndAssignRoom',
                    status: permissions.has(Permission.CREATE_AND_ASSIGN_ROOM),
                    description: 'Controls ability to run \'Create & Assign Rooms\' procedure.'
                },
                {
                    title: 'Assign Rooms',
                    id: Permission.ASSIGN_ROOMS,
                    intlId: 'oe.permission.assignRooms',
                    status: permissions.has(Permission.ASSIGN_ROOMS),
                    description: 'Controls access to Assign Rooms procedure on Schedule tab.'
                }
            ]
        },
        {
            title: 'Operators Management',
            id: 'oe.common.operatorsManagement',
            permissions: [
                {
                    title: 'Upload Operators',
                    id: Permission.UPLOAD_OPERATORS,
                    intlId: 'oe.permission.uploadOperators',
                    status: permissions.has(Permission.UPLOAD_OPERATORS),
                    description: 'Controls ability to Upload Operators CSV.'
                }
            ]
        },
        {
            title: 'Organization & Attendee Management',
            id: 'oe.organizationAndAttendee.management',
            permissions: [
                {
                    title: 'Attendee Upload',
                    id: Permission.ATTENDEE_UPLOAD,
                    intlId: 'oe.permission.attendeeUpload',
                    status: permissions.has(Permission.ATTENDEE_UPLOAD),
                    description: 'Controls ability to upload Attendees CSV file.'
                },
                {
                    title: 'Access Codes Management',
                    id: Permission.ACCESS_CODES_MANAGEMENT,
                    intlId: 'oe.permission.accessCodesManagement',
                    status: permissions.has(Permission.ACCESS_CODES_MANAGEMENT),
                    description: 'Controls ability to generate, upload and edit attendees\' Access Codes.'
                },
                {
                    title: 'Create Attendee',
                    id: Permission.CREATE_ATTENDEE,
                    intlId: 'oe.permissions.createAttendee',
                    status: permissions.has(Permission.CREATE_ATTENDEE),
                    description: 'Controls ability to create a new Event Attendee.'
                },
                {
                    title: 'Update Attendee',
                    id: Permission.UPDATE_ATTENDEE,
                    intlId: 'oe.permissions.updateAttendee',
                    status: permissions.has(Permission.UPDATE_ATTENDEE),
                    description: 'Controls ability to edit Event Attendee details.'
                },
                {
                    title: 'Delete Attendee',
                    id: Permission.DELETE_ATTENDEE,
                    intlId: 'oe.permissions.deleteAttendee',
                    status: permissions.has(Permission.DELETE_ATTENDEE),
                    description: 'Controls ability to delete an Event Attendee permanently.'
                },
                {
                    title: 'Bulk Generate Presenter Links',
                    id: Permission.BULK_GENERATE_ORGANIZATIONS_LINKS,
                    intlId: 'oe.permissions.generateOrganizationsLinks',
                    status: permissions.has(Permission.BULK_GENERATE_ORGANIZATIONS_LINKS),
                    description: 'Controls ability to generate organizations presenter and participant links.'
                },
                {
                    title: 'Create Organization',
                    id: Permission.CREATE_ORGANIZATION,
                    intlId: 'oe.permissions.createOrganizationDescription',
                    status: permissions.has(Permission.CREATE_ORGANIZATION),
                    description: 'Controls ability to create organizations on the event.'
                },
                {
                    title: 'Update Organization',
                    id: Permission.UPDATE_ORGANIZATION,
                    intlId: 'oe.permissions.updateOrganizationDescription',
                    status: permissions.has(Permission.UPDATE_ORGANIZATION),
                    description: 'Controls ability to edit organizations on the event.'
                },
                {
                    title: 'Delete Organization',
                    id: Permission.DELETE_ORGANIZATION,
                    intlId: 'oe.permissions.deleteOrganizationDescription',
                    status: permissions.has(Permission.DELETE_ORGANIZATION),
                    description: 'Controls ability to delete organizations on the event.'
                },
                {
                    title: 'Attendee Download',
                    id: Permission.ATTENDEE_DOWNLOAD,
                    intlId: 'oe.permissions.attendeeDownload',
                    status: permissions.has(Permission.ATTENDEE_DOWNLOAD),
                    description: 'Controls ability to download Attendees CSV file.'
                },
                {
                    title: 'Presenter Links Download',
                    id: Permission.PRESENTER_LINK_DOWNLOAD,
                    intlId: 'oe.permissions.presenterLinksDownload',
                    status: permissions.has(Permission.PRESENTER_LINK_DOWNLOAD),
                    description: 'Controls ability to download Presenter Links CSV file.'
                },
                {
                    title: 'Manage mapping of event attendee to OE Contact Database',
                    id: Permission.MANAGE_ATTENDEE_ROLODEX_MAPPING,
                    intlId: 'oe.permissions.manageAttendeeRolodexMapping',
                    status: permissions.has(Permission.MANAGE_ATTENDEE_ROLODEX_MAPPING),
                    description: 'Allows the user to add, edit or remove a mapping of an event attendee to a contact in the OE Contact database.'
                }
            ]
        },
        {
            title: 'OE Contact Profile Data Management',
            id: 'oe.contactProfileDataManagement.management',
            permissions: [
                {
                    title: 'Create OE Contact Profile',
                    id: Permission.CREATE_ROLODEX_CONTACT,
                    intlId: 'oe.permissions.createRolodexContact',
                    status: permissions.has(Permission.CREATE_ROLODEX_CONTACT),
                    description: 'Allows the user to create a contact in the OE Contact database.'
                },
                {
                    title: 'Edit OE Contact Profile',
                    id: Permission.EDIT_ROLODEX_CONTACT,
                    intlId: 'oe.permissions.editRolodexContact',
                    status: permissions.has(Permission.EDIT_ROLODEX_CONTACT),
                    description: 'Allows the user to edit a contact in the OE Contact database.'
                },
                {
                    title: 'View OE Data Organizations tab',
                    id: Permission.VIEW_OE_DATA_ORGANIZATIONS,
                    intlId: 'oe.permissions.viewOeDataOrganizations',
                    status: permissions.has(Permission.VIEW_OE_DATA_ORGANIZATIONS),
                    description: 'Allows the user to see the OE Data Organizations tab and search OE organizations.'
                }
            ]
        },
        {
            title: 'Event visibility',
            id: 'oe.event.eventVisibility',
            permissions: [
                {
                    title: 'View all events',
                    id: Permission.VIEW_ALL_EVENTS,
                    intlId: 'oe.permission.viewAllEvents',
                    status: permissions.has(Permission.VIEW_ALL_EVENTS),
                    description: 'Allows user to access \'Restricted\' Events without being assigned as a Coordinator or Delivery Team member for that event.'
                },
                {
                    title: 'Access Completed Events',
                    id: Permission.ACCESS_COMPLETED_EVENTS,
                    intlId: 'oe.permission.accessCompletedEvents',
                    status: permissions.has(Permission.ACCESS_COMPLETED_EVENTS),
                    description: 'Allows user to access Events in \'Delivered\' status.'
                },
            ]
        },
        {
            title: 'OE Podium',
            id: 'oe.meeting.podium',
            permissions: [
                {
                    title: 'Manage Podium Access',
                    id: Permission.MANAGE_PODIUM_ACCESS,
                    intlId: 'oe.permission.managePodiumAccess',
                    status: permissions.has(Permission.MANAGE_PODIUM_ACCESS),
                    description: 'Allows the user to enable and configure the Podium link for a meeting.'
                }
            ]
        },
        {
            title: 'Event Hubs',
            id: 'oe.event.hubs',
            permissions: [
                {
                    title: 'Event Hubs tab',
                    id: Permission.EVENT_HUBS_TAB,
                    intlId: 'oe.permission.eventHubsTab',
                    status: permissions.has(Permission.EVENT_HUBS_TAB),
                    description: 'Allows the user to access the Event Hubs tab and see the list of Hubs in an Account.'
                },
                {
                    title: 'Create Event Hub',
                    id: Permission.CREATE_EVENT_HUB,
                    intlId: 'oe.permission.createEventHub',
                    status: permissions.has(Permission.CREATE_EVENT_HUB),
                    description: 'Allows the user to create a new Event Hub in an Account.'
                },
                {
                    title: 'Edit Event Hub',
                    id: Permission.EDIT_EVENT_HUB,
                    intlId: 'oe.permission.editEventHub',
                    status: permissions.has(Permission.EDIT_EVENT_HUB),
                    description: 'Allows the user to edit an existing Event Hub in an Account.'
                },
                {
                    title: 'Delete Event Hub',
                    id: Permission.DELETE_EVENT_HUB,
                    intlId: 'oe.permission.deleteEventHub',
                    status: permissions.has(Permission.DELETE_EVENT_HUB),
                    description: 'Allows the user to delete an existing Event Hub in an Account.'
                },
                {
                    title: 'Manage Hub subdomain',
                    id: Permission.MANAGE_HUB_SUBDOMAIN,
                    intlId: 'oe.permission.manageHubSubdomain',
                    status: permissions.has(Permission.MANAGE_HUB_SUBDOMAIN),
                    description: 'Allows the user to set and update the subdomain for the Hub URL.'
                },
                {
                    title: 'View Hub Attendees',
                    id: Permission.VIEW_HUB_ATTENDEES,
                    intlId: 'oe.permission.viewHubAttendees',
                    status: permissions.has(Permission.VIEW_HUB_ATTENDEES),
                    description: 'Allows the user to view the Attendees tab within a Hub.'
                },
                {
                    title: 'Create Hub Attendee',
                    id: Permission.CREATE_HUB_ATTENDEES,
                    intlId: 'oe.permission.createHubAttendees',
                    status: permissions.has(Permission.CREATE_HUB_ATTENDEES),
                    description: 'Allows the user to create Attendees within a Hub.'
                },
                {
                    title: 'Edit Hub Attendee',
                    id: Permission.EDIT_HUB_ATTENDEES,
                    intlId: 'oe.permission.editHubAttendees',
                    status: permissions.has(Permission.EDIT_HUB_ATTENDEES),
                    description: 'Allows the user to edit Attendees within a Hub.'
                },
                {
                    title: 'Delete Hub Attendee',
                    id: Permission.DELETE_HUB_ATTENDEES,
                    intlId: 'oe.permission.deleteHubAttendees',
                    status: permissions.has(Permission.DELETE_HUB_ATTENDEES),
                    description: 'Allows the user to delete Attendees from a Hub.'
                },
                {
                    title: 'Create Hub Organization',
                    id: Permission.CREATE_HUB_ORGANIZATION,
                    intlId: 'oe.permission.createHubOrganization',
                    status: permissions.has(Permission.CREATE_HUB_ORGANIZATION),
                    description: 'Allows the user to create Organizations within a Hub.'
                },
                {
                    title: 'Edit Hub Organization',
                    id: Permission.EDIT_HUB_ORGANIZATION,
                    intlId: 'oe.permission.editHubOrganization',
                    status: permissions.has(Permission.EDIT_HUB_ORGANIZATION),
                    description: 'Allows the user to edit Organizations within a Hub.'
                },
                {
                    title: 'Delete Hub Organization',
                    id: Permission.DELETE_HUB_ORGANIZATION,
                    intlId: 'oe.permission.deleteHubOrganization',
                    status: permissions.has(Permission.DELETE_HUB_ORGANIZATION),
                    description: 'Allows the user to delete Organizations from a Hub'
                },
                {
                    title: 'Manage Pages and Modules',
                    id: Permission.HUB_MANAGE_PAGES_AND_MODULES,
                    intlId: 'oe.permission.hubManagePagesAndModules',
                    status: permissions.has(Permission.HUB_MANAGE_PAGES_AND_MODULES),
                    description: 'Allows the user to build the Hub site by creating and editing pages and their content modules.'
                },
                {
                    title: 'Configure Event Hub Registration & Sign In',
                    id: Permission.CONFIGURE_EVENT_HUB_REGISTRATION_AND_SIGN_IN,
                    intlId: 'oe.permission.configureEventHubRegistrationAndSignIn',
                    status: permissions.has(Permission.CONFIGURE_EVENT_HUB_REGISTRATION_AND_SIGN_IN),
                    description: 'Allows the user to configure the registration and sign in pages of a Hub.'
                },
                {
                    title: 'Hub Website Builder',
                    id: Permission.HUB_WEBSITE_BUILDER,
                    intlId: 'oe.permission.hubWebsiteBuilder',
                    status: permissions.has(Permission.HUB_WEBSITE_BUILDER),
                    description: 'Allows the user to open the Hub website builder to configure the Hub site.'
                },
                {
                    title: 'Manage Hub Branding',
                    id: Permission.MANAGE_HUB_BRANDING,
                    intlId: 'oe.permission.manageHubBranding',
                    status: permissions.has(Permission.MANAGE_HUB_BRANDING),
                    description: 'Allows the user to configure branding elements such as color, logos, images, and custom CSS.'
                }
            ]
        },
        {
            title: 'AI',
            id: 'oe.permission.ai',
            permissions: [
                {
                    title: 'MCP Server',
                    id: Permission.MCP_SERVER,
                    intlId: 'oe.permission.mcpServer',
                    status: permissions.has(Permission.MCP_SERVER),
                    description: 'Controls access to the OE Central MCP Server.'
                },
                {
                    title: 'Manage Agent Memory',
                    id: Permission.MANAGE_AGENT_MEMORY,
                    intlId: 'oe.permission.manageAgentMemory',
                    status: permissions.has(Permission.MANAGE_AGENT_MEMORY),
                    description: 'Allows the user to view and edit the Agent Memory of an event.'
                },
                {
                    title: 'AI Assistant',
                    id: Permission.AI_ASSISTANT,
                    intlId: 'oe.permission.aiAssistant',
                    status: permissions.has(Permission.AI_ASSISTANT),
                    description: 'Allows the user to access the OE Assistant.'
                }
            ]
        }
    ];
};
